import taskTemplatesPickerFieldUrl from './taskTemplatesPickerField.html';

class TaskTemplatesPickerFieldCtrl {
  excludedTemplates = null;
  isReadyToBeShown = false;
  areTemplatesReady = false;
  areInstancesReady = false;
  isAddTemplateModalOpen = false;
  searchQuery = '';

  constructor(
    EventCollection,
    ShiftTasksService,
    TaskTemplatesService,
    ShiftTasksReactBridge,
    focus,
    $uibModal,
    $q,
    $rootScope
  ) {
    'ngInject';

    Object.assign(this, {
      EventCollection,
      ShiftTasksService,
      TaskTemplatesService,
      ShiftTasksReactBridge,
      focus,
      $uibModal,
      $q,
      $rootScope,
    });
  }

  $onInit() {
    if (this.isFeatureAvailable) {
      this.loadTemplates();

      if (!this.instances && this.shiftId && !this.isTemplate) {
        this.loadInstances();
      } else {
        this.areInstancesReady = true;
      }
    }

    if (this.inlineCreateEnabled) {
      this.createCallback = async () => {
        if (this.templates?.length) {
          const promises = this.templates
            .filter((template) => !template.id)
            .map(async (template) => await this.TaskTemplatesService.create(template));
          this.templates = await this.$q.all(promises);
          return this.templates;
        }
        return [];
      };
    }
  }

  get taskTemplates() {
    return this.TaskTemplatesService.allTemplates;
  }

  get isFeatureAvailable() {
    return this.ShiftTasksService.isFeatureEnabled;
  }

  get hasUsedAllAvailableTemplates() {
    const nonDeleted = this.templatesAndInstances?.filter((template) => !template.deleted);

    return (
      nonDeleted?.length &&
      this.taskTemplates?.length &&
      nonDeleted.length >= this.taskTemplates.length
    );
  }

  async loadTemplates() {
    await this.TaskTemplatesService.loadTemplates();
    this.areTemplatesReady = true;
    this.setupComponent();
  }

  async loadInstances() {
    const { taskInstances, taskTemplates } = await this.EventCollection.getShiftDetails(
      this.shiftId
    );
    this.instances = taskInstances;
    this.templates = taskTemplates;
    this.areInstancesReady = true;
    this.setupComponent();
  }

  setupComponent() {
    if (this.areTemplatesReady && this.areInstancesReady) {
      this.templatesAndInstances = this.getTemplatesAndInstancesInOneModel();
      this.excludedTemplates = this.getExcludedTemplates();
      this.isReadyToBeShown = true;
    }
  }

  getTemplatesAndInstancesInOneModel() {
    const templates = this.getTemplatesWithDetails();

    if (this.instances && templates) {
      return [...this.instances, ...this.getNonInstantiatedTemplates(templates, this.instances)];
    }

    return templates || [];
  }

  getTemplatesWithDetails() {
    return this.templates?.map(({ id, title, ...restOfTheTemplateProps }) => {
      if (!title) {
        const template = this.TaskTemplatesService.findById(id);
        return { id, title: template?.title, ...restOfTheTemplateProps };
      }

      return { id, title, ...restOfTheTemplateProps };
    });
  }

  getNonInstantiatedTemplates(templates, instances) {
    return templates.filter(
      ({ id }) => !id || !instances.some(({ taskTemplateId }) => taskTemplateId === id)
    );
  }

  getExcludedTemplates() {
    const excluded = [];
    this.templatesAndInstances.forEach((taskOrTemplate) => {
      const { taskTemplateId, deleted } = taskOrTemplate;
      if (!deleted && taskTemplateId) {
        excluded.push(taskTemplateId);
      }
    });
    return excluded;
  }

  onTaskTemplateSelect = (template) => {
    if (!this.templates) {
      this.templates = [];
    }

    const taskInstancePreviouslyAdded = this.instances?.find(
      ({ taskTemplateId }) => taskTemplateId === template.id
    );
    if (taskInstancePreviouslyAdded) {
      taskInstancePreviouslyAdded.deleted = false;
    }

    const templatePreviouslyAdded =
      template.id && this.templates.find(({ id }) => id === template.id);
    if (templatePreviouslyAdded) {
      templatePreviouslyAdded.deleted = false;
    } else {
      this.templates = [...this.templates, { ...template }];
    }

    this.templatesAndInstances = this.getTemplatesAndInstancesInOneModel();
    this.excludedTemplates = this.getExcludedTemplates();
    this.searchQuery = '';
    this.form.$setDirty();
  };

  onRemove({ id, taskTemplateId, title }) {
    if (!id && !taskTemplateId) {
      this.templates = this.templates.filter((template) => template.title !== title);
    } else if (taskTemplateId) {
      this.onTaskInstanceRemove(id);
    } else {
      this.onTaskTemplateRemove(id);
    }
    this.templatesAndInstances = this.getTemplatesAndInstancesInOneModel();
    this.excludedTemplates = this.getExcludedTemplates();
  }

  onTaskInstanceRemove(taskInstanceId) {
    const taskInstance = this.instances.find(({ id }) => id === taskInstanceId);
    if (taskInstance) {
      taskInstance.deleted = true;
      const template = this.templates.find(({ id }) => id === taskInstance.taskTemplateId);
      if (template) {
        template.deleted = true;
      }
      this.form.$setDirty();
    }
  }

  onTaskTemplateRemove(taskTemplateId) {
    const template = this.templates.find(({ id }) => id === taskTemplateId);
    if (template) {
      template.deleted = true;
      this.form.$setDirty();
    }
  }

  onAddTaskModalClose = () => {
    this.isAddTemplateModalOpen = false;
    this.newTemplate = null;
  };

  async createAndSelectTemplate(title = '') {
    this.newTemplate = {
      title,
    };
    this.isAddTemplateModalOpen = true;
  }

  onEnter() {
    if (this.taskTemplatesSearchResults.length) {
      const [template] = this.taskTemplatesSearchResults;
      this.onTaskTemplateSelect(template);
    } else if (this.searchQuery && this.inlineCreateEnabled) {
      this.createAndSelectTemplate(this.searchQuery);
    }
    this.searchQuery = '';
    this.isLookupOpen = false;
  }
}

export default {
  templateUrl: taskTemplatesPickerFieldUrl,
  controller: TaskTemplatesPickerFieldCtrl,
  controllerAs: 'ctrl',
  require: {
    form: '^^form',
  },
  bindings: {
    templates: '=',
    instances: '=?',
    shiftId: '<',
    createCallback: '=',
    isTemplate: '<',
    inlineCreateEnabled: '<?',
  },
};
